import { error, assert } from './logger.js';
import { storage } from './storage.js';
import { window } from '../window.js';

const kGoogleMapsMapProvider = 'googlemaps';
const kLeafletMapProvider = 'leaflet';
const kDefaultMapProvider = kGoogleMapsMapProvider;

const yes = {
  normalize: v => !!v,
  defaultValue: true,
};

const no = {
  normalize: v => !!v,
  defaultValue: false,
};

const settingsMeta = {
  // Turns on/off server-clinet app sync.
  appSyncDisabled: no,

  // Turns on/off logs sending to analytics server.
  analyticsDisabled: yes,

  // No console (including errors).
  consoleDisabled: no,

  // No console logging.
  consoleLogDisabled: no,

  // No stacks printed when logging.
  consoleStackDisabled: yes,

  // Turns on/off history navigation.
  historyDisabled: no,

  // Get/set map provider.
  mapProvider: {
    normalize: v => (v == kLeafletMapProvider && v) || kDefaultMapProvider,
    defaultValue: kDefaultMapProvider,
  },

  // If true then media processing dialog will be closed automatically,
  // when media processing is finished.
  mediaProcessingDialogAutoHide: no,

  // Sets a time offset in seconds for picture dates to align them with
  // a related gpx track on the map media layer. Used when gps device
  // and photocamera are out of sync.
  mediaTimeOffset: {
    normalize: v => parseInt(v),
    defaultValue: 0,
  },

  // Turns on/off the store.
  storeDisabled: yes,

  // Throws on assertion or error.
  throwOnAssertOrError: no,
};

/**
 * Application settings.
 */
class Settings {
  constructor() {
    // Define getters and setters.
    for (let setting in settingsMeta) {
      const that = this;
      Object.defineProperty(this, setting, {
        get() {
          return that.get(setting);
        },
        set(v) {
          that.set(setting, v);
        },
        enumerable: true,
      });
    }
  }

  // Returns the setting.
  get(setting) {
    return storage.has(setting)
      ? storage.get(setting)
      : window[setting] || settingsMeta[setting].defaultValue;
  }

  // Sets the setting or settings given as an object of key, value pairs.
  set(setting_or_settings, maybe_value) {
    let settings = setting_or_settings;
    if (typeof setting_or_settings == 'string') {
      settings = {};
      settings[setting_or_settings] = maybe_value;
    }

    for (let setting in settings) {
      let value = settings[setting];
      if (!(setting in settingsMeta)) {
        error(`[settings] unsupported '${setting}' setting`);
        return;
      }

      value === undefined
        ? storage.delete(setting)
        : storage.set(setting, settingsMeta[setting].normalize(value));

      this.#uiHooks[setting]?.(value);
      this.#hooks[setting]?.forEach(hook => hook(value));
    }
  }

  hookOnChange(setting, hook) {
    if (!(setting in this.#hooks)) {
      this.#hooks[setting] = [];
    }
    this.#hooks[setting].push(hook);
  }

  // Build an UI element representing a setting/preference.
  bindUI(setting) {
    let el = window.document.querySelector(`[pref=${setting}]`);
    assert(el, `[settings] no ${setting} element found`);
    if (el && el.getAttribute('type') == 'checkbox') {
      el.addEventListener('click', () => this.set(setting, !this.get(setting)));
      this.#uiHooks[setting] = v => (el.checked = v);
      this.#uiHooks[setting](this.get(setting));
    }
  }

  #hooks = {};
  #uiHooks = {};
}

export const settings = new Settings();
